import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import DSBox from '../../../../../design-system/DSBox';
import DSTextField from '../../../../../design-system/DSTextField';
import DSTextArea from '../../../../../design-system/DSTextArea';
import { useTheme } from '@mui/material';
import DSLabelTypography from '../../../../../design-system/Typography/DSLabelTypography';
import {
  COLLECTION_DESCRIPTION_LIMIT,
  COLLECTION_NAME_LIMIT,
} from '../../../../../consts/textFieldsLimitations';
import DSIconButton from '../../../../../design-system/DSIconButton';
import DSHelpOutline from '../../../../../design-system/DSHelpOutline';
import DSTooltip from '../../../../../design-system/DSTooltip';
import DSSwitch from '../../../../../design-system/DSSwitch';
import DSRadioGroup from '../../../../../design-system/DSRadioGroup';
import DSFormControl from '../../../../../design-system/DSFormControl';
import DSFormControlLabel from '../../../../../design-system/DSFormControlLabel';
import DSRadio from '../../../../../design-system/DSRadio';
import {
  LOGO_FADE_SPEC,
  COLLECTION_COVER_SPEC,
  COLLECTION_LOGOTYPE_SPEC,
  COLLECTION_LOGO_SPEC,
  COLLECTION_BACKGROUND_SPEC,
  COLLECTION_DESCRIPTION_SPEC,
  LOGO_TYPE_SPEC_IMAGE,
  BACKGROUND_SPEC_IMAGE,
  CONTRAST_COLOR_SPEC_IMAGE,
} from '../../../../../consts/specs';
import LabelsInput from '../LabelsInput';
import CollectionAssetFileInput from '../CollectionAssetsStates/CollectionAssetFileInput';
import PendulumLogoSizeModal from '../PendulumLogoSizeModal';
import DSButton from '../../../../../design-system/DSButton';
import ColorPickerButton from '../../../../Klydos/ColorPicker/components/ColorPickerButton';
import { ColorValidator } from '../../../../../utils/contrastCheck';
import useAppContext from '../../../../../hooks/useAppContext';
import useCanSetFullLogo from '../../../../../hooks/useCanSetFullLogo';
import CollectionDisplayKlydosSelector from '../CollectionDisplayKlydosSelector';
import CollectionMockupSelector from '../CollectionMockupSelector';
import { PopulatedKlydo } from '../../../../../api/klydo';
import {
  PendulumLogoType,
  PENDULUM_BOB_DIAMETER_PX,
} from '../../../../../consts/pendulumLogo';

type CollectionAssetsTabProps = {
  collectionName: string;
  setCollectionName: (name: string) => void;
  collectionDescription: string;
  setCollectionDescription: (description: string) => void;
  labels: string[];
  handleAddLabel: (label: string) => void;
  handleRemoveLabel: (index: number) => void;
  collectionCover: string;
  collectionCoverFileName: string;
  isUploadingCover: boolean;
  onCoverFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCoverRemove: () => void;
  collectionLogotype: string;
  collectionLogotypeFileName: string;
  isUploadingLogotype: boolean;
  onLogotypeFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLogotypeRemove: () => void;
  collectionLogo: string;
  collectionLogoFileName: string;
  isUploadingLogo: boolean;
  onLogoFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLogoRemove: () => void;
  collectionThumbnailBackground: string;
  collectionThumbnailBackgroundFileName: string;
  isUploadingThumbnailBackground: boolean;
  onThumbnailBackgroundFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onThumbnailBackgroundRemove: () => void;
  collectionHoldPendulumLogo: boolean;
  setCollectionHoldPendulumLogo: (value: boolean) => void;
  collectionPendulumLogoSize: number | null | undefined;
  setCollectionPendulumLogoSize: (value: number | null) => void;
  // 'sized' (default) pads the logo to collectionPendulumLogoSize; 'full'
  // (B2B-only, or an admin impersonating the artist) bakes it to cover-crop
  // the whole bob instead - see resolveLogoDiameterPx.
  collectionPendulumLogoType: PendulumLogoType;
  setCollectionPendulumLogoType: (value: PendulumLogoType) => void;
  // Sizing is baked into a real Cloudinary asset once, at approval
  // (klydo-api, bakePendulumLogoAtSeal) - it can't run again, so changing
  // the size after that point has no effect even though the picker still
  // renders.
  isApprovedCollection: boolean;
  collectionThumbnailContrastColor: string;
  setCollectionThumbnailContrastColor: (color: string) => void;
  collectionKlydos: PopulatedKlydo[];
  collectionThumbnailKlydos: string[];
  setCollectionThumbnailKlydos: (klydoIds: string[]) => void;
  collectionMockTemplateId: string;
  setCollectionMockTemplateId: (templateId: string) => void;
  collectionCredits: string;
  setCollectionCredits: (credits: string) => void;
};

const CollectionAssetsTab = ({
  collectionName,
  setCollectionName,
  collectionDescription,
  setCollectionDescription,
  labels,
  handleAddLabel,
  handleRemoveLabel,
  collectionCover,
  collectionCoverFileName,
  isUploadingCover,
  onCoverFileChange,
  onCoverRemove,
  collectionLogotype,
  collectionLogotypeFileName,
  isUploadingLogotype,
  onLogotypeFileChange,
  onLogotypeRemove,
  collectionLogo,
  collectionLogoFileName,
  isUploadingLogo,
  onLogoFileChange,
  onLogoRemove,
  collectionThumbnailBackground,
  collectionThumbnailBackgroundFileName,
  isUploadingThumbnailBackground,
  onThumbnailBackgroundFileChange,
  onThumbnailBackgroundRemove,
  collectionHoldPendulumLogo,
  setCollectionHoldPendulumLogo,
  collectionPendulumLogoSize,
  setCollectionPendulumLogoSize,
  collectionPendulumLogoType,
  setCollectionPendulumLogoType,
  isApprovedCollection,
  collectionThumbnailContrastColor,
  setCollectionThumbnailContrastColor,
  collectionKlydos,
  collectionThumbnailKlydos,
  setCollectionThumbnailKlydos,
  collectionMockTemplateId,
  setCollectionMockTemplateId,
  collectionCredits,
  setCollectionCredits,
}: CollectionAssetsTabProps) => {
  const { spacing, palette } = useTheme();
  const { isAdmin } = useAppContext();
  const canSetFullLogo = useCanSetFullLogo();
  const [contrastIsValid, setContrastIsValid] = useState<boolean | null>(null);
  const [colorValidator] = useState(() => new ColorValidator());
  const [isPendulumSizeModalOpen, setIsPendulumSizeModalOpen] = useState(false);

  useEffect(() => {
    if (!collectionThumbnailBackground && collectionThumbnailContrastColor) {
      setCollectionThumbnailContrastColor('');
      setContrastIsValid(null);
    }
  }, [
    collectionThumbnailBackground,
    collectionThumbnailContrastColor,
    setCollectionThumbnailContrastColor,
  ]);

  const handleContrastColorChange = useCallback(
    async (newColor: string) => {
      if (!collectionThumbnailBackground) {
        return;
      }

      try {
        const dominantColor =
          await colorValidator.extractDominantColorFromImage(
            collectionThumbnailBackground,
          );
        const ratio = colorValidator.calculateContrastRatio(
          dominantColor,
          newColor,
        );
        const isValid = colorValidator.hasGoodContrast(ratio);

        if (isValid || isAdmin) {
          setCollectionThumbnailContrastColor(newColor);
          setContrastIsValid(true);
        } else {
          setContrastIsValid(false);
        }
      } catch (error) {
        console.error('Error checking contrast:', error);
        setContrastIsValid(null);
      }
    },
    [
      collectionThumbnailBackground,
      setCollectionThumbnailContrastColor,
      colorValidator,
    ],
  );

  const onCollectionNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setCollectionName(e.target.value);
    },
    [setCollectionName],
  );

  const onCollectionDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setCollectionDescription(e.target.value);
    },
    [setCollectionDescription],
  );

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(4),
        width: '100%',
      }}
    >
      <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}>
        <DSLabelTypography>*Name</DSLabelTypography>
        <DSTextField
          value={collectionName}
          placeholder="My Collection"
          onChange={onCollectionNameChange}
          fullWidth
          slotProps={{
            input: {
              inputProps: { maxLength: COLLECTION_NAME_LIMIT },
            },
          }}
        />
      </DSBox>

      <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}>
        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing(0.5),
          }}
        >
          <DSLabelTypography>*Tags</DSLabelTypography>
          <DSTooltip title="Search and add tags to categorize your collection">
            <DSIconButton
              sx={{
                height: spacing(2),
                width: spacing(2),
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: palette.text.primary,
                },
                color: palette.text.secondary,
                transition: 'color 0.3s ease',
              }}
            >
              <DSHelpOutline sx={{ fontSize: 16 }} />
            </DSIconButton>
          </DSTooltip>
        </DSBox>

        <LabelsInput
          selectedLabels={labels}
          onAddLabel={handleAddLabel}
          onRemoveLabel={handleRemoveLabel}
        />
      </DSBox>

      <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}>
        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing(0.5),
          }}
        >
          <DSLabelTypography>*Description</DSLabelTypography>
          <DSTooltip title={COLLECTION_DESCRIPTION_SPEC}>
            <DSIconButton
              sx={{
                height: spacing(2),
                width: spacing(2),
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: palette.text.primary,
                },
                color: palette.text.secondary,
                transition: 'color 0.3s ease',
              }}
            >
              <DSHelpOutline sx={{ fontSize: 16 }} />
            </DSIconButton>
          </DSTooltip>
        </DSBox>
        <DSTextArea
          value={collectionDescription}
          onChange={onCollectionDescriptionChange}
          maxRows={6}
          fullWidth
          placeholder="My Collection is special since..."
          slotProps={{
            input: {
              inputProps: { maxLength: COLLECTION_DESCRIPTION_LIMIT },
            },
          }}
        />
      </DSBox>

      <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}>
        <DSLabelTypography>Additional Credits</DSLabelTypography>
        <DSTextArea
          value={collectionCredits}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setCollectionCredits(e.target.value)
          }
          maxRows={4}
          fullWidth
          placeholder="Credits and acknowledgements..."
        />
      </DSBox>

      <DSBox
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(2, 1fr)',
          },
          gap: spacing(3),
          width: '100%',
        }}
      >
        <CollectionDisplayKlydosSelector
          collectionKlydos={collectionKlydos}
          selectedKlydoIds={collectionThumbnailKlydos}
          onSelectedKlydosChange={setCollectionThumbnailKlydos}
        />

        <CollectionMockupSelector
          selectedMockTemplateId={collectionMockTemplateId}
          onMockTemplateIdChange={setCollectionMockTemplateId}
        />
      </DSBox>

      <DSBox
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: spacing(3),
          width: '100%',
        }}
      >
        <CollectionAssetFileInput
          label="Cover"
          fileSpecsText={`2000 × 2000 px | 300 dpi\n(PNG, JPEG, JPG)`}
          acceptedFormats=".png, .jpg, .jpeg"
          helpTooltipContent={COLLECTION_COVER_SPEC}
          secureUrl={collectionCover}
          fileName={collectionCoverFileName}
          isLoading={isUploadingCover}
          onFileChange={onCoverFileChange}
          onRemoveFile={onCoverRemove}
          required
        />

        <CollectionAssetFileInput
          label="Logotype"
          fileSpecsText={`1000 × 300 px 300 dpi\n(PNG). original`}
          acceptedFormats=".png"
          helpTooltipContent={
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <span style={{ whiteSpace: 'pre-line' }}>
                {COLLECTION_LOGOTYPE_SPEC}
              </span>
              <img
                src={LOGO_TYPE_SPEC_IMAGE}
                alt="Logotype specification"
                style={{ maxWidth: '300px', borderRadius: '8px' }}
              />
            </DSBox>
          }
          secureUrl={collectionLogotype}
          fileName={collectionLogotypeFileName}
          isLoading={isUploadingLogotype}
          onFileChange={onLogotypeFileChange}
          onRemoveFile={onLogotypeRemove}
          required
        />

        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(1),
          }}
        >
          <CollectionAssetFileInput
            label="Logo symbol"
            fileSpecsText={`1000 × 1000 px 300 dpi\n(PNG). Monochromatic White`}
            acceptedFormats=".png"
            helpTooltipContent={COLLECTION_LOGO_SPEC}
            secureUrl={collectionLogo}
            fileName={collectionLogoFileName}
            isLoading={isUploadingLogo}
            onFileChange={onLogoFileChange}
            onRemoveFile={onLogoRemove}
            required
          />
          <DSBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing(0.5),
            }}
          >
            <DSLabelTypography>Fade</DSLabelTypography>
            <DSTooltip title={LOGO_FADE_SPEC}>
              <DSIconButton
                sx={{
                  height: spacing(2),
                  width: spacing(2),
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: palette.text.primary,
                  },
                  color: palette.text.secondary,
                  transition: 'color 0.3s ease',
                }}
              >
                <DSHelpOutline sx={{ fontSize: 16 }} />
              </DSIconButton>
            </DSTooltip>
            <DSSwitch
              checked={!collectionHoldPendulumLogo}
              onChange={(e) => setCollectionHoldPendulumLogo(!e.target.checked)}
            />
          </DSBox>
          {canSetFullLogo && (
            <DSFormControl>
              <DSRadioGroup
                row
                value={collectionPendulumLogoType}
                onChange={(_, value) =>
                  setCollectionPendulumLogoType(value as PendulumLogoType)
                }
              >
                <DSFormControlLabel
                  value="sized"
                  control={<DSRadio size="small" />}
                  label="Sized logo"
                  sx={{
                    '.MuiFormControlLabel-label': {
                      color: palette.text.secondary,
                    },
                  }}
                />
                <DSFormControlLabel
                  value="full"
                  control={<DSRadio size="small" />}
                  label="Full pendulum image"
                  sx={{
                    '.MuiFormControlLabel-label': {
                      color: palette.text.secondary,
                    },
                  }}
                />
              </DSRadioGroup>
            </DSFormControl>
          )}
          {canSetFullLogo && collectionPendulumLogoType === 'sized' && (
            <>
              <DSTooltip
                title={
                  isApprovedCollection
                    ? "Size is locked in once a collection is approved and can't be changed after that."
                    : ''
                }
              >
                <DSButton
                  variant="text"
                  size="small"
                  disabled={!collectionLogo || isApprovedCollection}
                  onClick={() => setIsPendulumSizeModalOpen(true)}
                  sx={{ alignSelf: 'flex-start', padding: 0, minWidth: 'auto' }}
                >
                  Adjust pendulum size (
                  {collectionPendulumLogoSize != null
                    ? `${collectionPendulumLogoSize}px`
                    : 'default'}
                  )
                </DSButton>
              </DSTooltip>
              <PendulumLogoSizeModal
                isOpen={isPendulumSizeModalOpen}
                onClose={() => setIsPendulumSizeModalOpen(false)}
                logoUrl={collectionLogo}
                initialSize={collectionPendulumLogoSize}
                onConfirm={setCollectionPendulumLogoSize}
                onReset={() => setCollectionPendulumLogoSize(null)}
              />
            </>
          )}
          {canSetFullLogo && collectionPendulumLogoType === 'full' && (
            <DSLabelTypography
              sx={{ color: palette.text.secondary, fontSize: spacing(1.5) }}
            >
              The Logo symbol image above will be cover-cropped to fill the
              whole pendulum bob - no separate size picker applies. Use a square
              image, at least {PENDULUM_BOB_DIAMETER_PX}×
              {PENDULUM_BOB_DIAMETER_PX}px - a smaller or non-square image will
              be upscaled (blurred) or have its edges cropped.
            </DSLabelTypography>
          )}
        </DSBox>

        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(1),
          }}
        >
          <CollectionAssetFileInput
            label="Thumbnail background"
            fileSpecsText={`2000 × 2000 px | 300 dpi\n(PNG, JPEG, JPG)`}
            acceptedFormats=".png, .jpg, .jpeg"
            helpTooltipContent={
              <DSBox
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <span style={{ whiteSpace: 'pre-line' }}>
                  {COLLECTION_BACKGROUND_SPEC}
                </span>
                <img
                  src={BACKGROUND_SPEC_IMAGE}
                  alt="Background specification"
                  style={{ maxWidth: '300px', borderRadius: '8px' }}
                />
              </DSBox>
            }
            secureUrl={collectionThumbnailBackground}
            fileName={collectionThumbnailBackgroundFileName}
            isLoading={isUploadingThumbnailBackground}
            onFileChange={onThumbnailBackgroundFileChange}
            onRemoveFile={onThumbnailBackgroundRemove}
            required
          />
          <DSBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: spacing(1),
            }}
          >
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(0.5),
                flex: 1,
              }}
            >
              <DSBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing(0.5),
                }}
              >
                <DSLabelTypography
                  color={contrastIsValid === false ? 'red' : undefined}
                >
                  *Contrast Color
                </DSLabelTypography>
                <DSTooltip
                  title={
                    <DSBox
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        alignItems: 'center',
                      }}
                    >
                      <span>
                        Select a contrast color for the thumbnail background
                      </span>
                      <img
                        src={CONTRAST_COLOR_SPEC_IMAGE}
                        alt="Contrast color specification"
                        style={{ maxWidth: '300px', borderRadius: '8px' }}
                      />
                    </DSBox>
                  }
                >
                  <DSIconButton
                    sx={{
                      height: spacing(2),
                      width: spacing(2),
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: palette.text.primary,
                      },
                      color: palette.text.secondary,
                      transition: 'color 0.3s ease',
                    }}
                  >
                    <DSHelpOutline sx={{ fontSize: 16 }} />
                  </DSIconButton>
                </DSTooltip>
              </DSBox>
              {contrastIsValid === false && (
                <DSLabelTypography color="red" sx={{ fontSize: spacing(1.5) }}>
                  Color contrast low. Try again
                </DSLabelTypography>
              )}
            </DSBox>
            <ColorPickerButton
              color={collectionThumbnailContrastColor}
              onColorChange={handleContrastColorChange}
              disabled={!collectionThumbnailBackground}
            />
          </DSBox>
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default CollectionAssetsTab;
